import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTeamMembers } from '../hooks/useSupabase';
import { useSite } from '../contexts/SiteContext';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from '../components/ImageWithFallback';
import EditableContent from '../components/EditableContent';
import ShareButton from '../components/ShareButton';
import PlayerModal from '../components/PlayerModal';
import PlayerSponsorBlock from '../components/PlayerSponsorBlock';
import * as FiIcons from 'react-icons/fi';

const {
  FiUsers,
  FiAward,
  FiCalendar,
  FiMapPin,
  FiStar,
  FiShield,
  FiTarget,
  FiArrowRight,
} = FiIcons;

const TeamPage = () => {
  const { data: staffMembers, loading: staffLoading } = useTeamMembers(true);
  const { data: players, loading: playersLoading } = useTeamMembers(false);
  const { siteConfig } = useSite();
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sortedStaff = useMemo(() => [...(staffMembers || [])].sort((a, b) => {
    const orderA = Number(a.sort_order ?? 0);
    const orderB = Number(b.sort_order ?? 0);
    return orderA !== orderB ? orderA - orderB : (a.name || '').localeCompare(b.name || '', 'el');
  }), [staffMembers]);

  const sortedPlayers = useMemo(() => [...(players || [])].sort((a, b) => {
    const orderA = Number(a.sort_order ?? 0);
    const orderB = Number(b.sort_order ?? 0);
    return orderA !== orderB ? orderA - orderB : (a.name || '').localeCompare(b.name || '', 'el');
  }), [players]);

  const boardMembers = useMemo(() => sortedStaff.filter((member) => member.staff_group === 'board'), [sortedStaff]);
  const technicalMembers = useMemo(() => sortedStaff.filter((member) => member.staff_group !== 'board'), [sortedStaff]);
  const president = useMemo(() => boardMembers.find((member) => member.is_president) || boardMembers[0] || null, [boardMembers]);
  const otherBoardMembers = useMemo(() => boardMembers.filter((member) => member.id !== president?.id), [boardMembers, president]);

  const closePlayer = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  const renderLoading = (dark = false) => (
    <div className="py-12 text-center">
      <div className={`mx-auto h-12 w-12 animate-spin rounded-full border-2 border-t-transparent ${dark ? 'border-white' : 'border-primary-600'}`} />
      <p className={`mt-4 ${dark ? 'text-white/70' : 'text-gray-600'}`}>Φόρτωση...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden bg-primary-700 py-16 text-white">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-4 text-4xl font-black md:text-5xl">
            <EditableContent page="team" section="header" contentKey="title" defaultValue="Η Ομάδα" tag="span" />
          </h1>
          <p className="text-xl text-primary-100">
            <EditableContent page="team" section="header" contentKey="subtitle" defaultValue="Γνωρίστε τη γυναικεία ομάδα του Α.Ο. Χανιά" tag="span" />
          </p>
        </div>
        <div className="absolute right-4 top-4 z-20"><ShareButton title="Η Ομάδα - Α.Ο. Χανιά" compact variant="outline" /></div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-center text-3xl font-black text-gray-900">
            <EditableContent page="team" section="overview" contentKey="title" defaultValue="Επισκόπηση Συλλόγου" tag="span" />
          </h2>

          <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border bg-gray-50 p-6 text-center">
              <SafeIcon icon={FiCalendar} className="mx-auto mb-4 h-8 w-8 text-primary-600" />
              <h3 className="mb-2 font-semibold"><EditableContent page="team" section="overview" contentKey="founded_label" defaultValue="Έτος Ίδρυσης" tag="span" /></h3>
              <p className="text-xl font-black text-primary-600">{siteConfig.stats_founding_year || '1945'}</p>
            </div>
            <div className="rounded-2xl border bg-gray-50 p-6 text-center">
              <SafeIcon icon={FiAward} className="mx-auto mb-4 h-8 w-8 text-primary-600" />
              <h3 className="mb-2 font-semibold"><EditableContent page="team" section="overview" contentKey="colors_label" defaultValue="Χρώματα" tag="span" /></h3>
              <p className="text-xl font-black text-primary-600"><EditableContent page="team" section="overview" contentKey="colors_value" defaultValue="Μπλε & Λευκό" tag="span" /></p>
            </div>
            <div className="rounded-2xl border bg-gray-50 p-6 text-center">
              <SafeIcon icon={FiMapPin} className="mx-auto mb-4 h-8 w-8 text-primary-600" />
              <h3 className="mb-2 font-semibold"><EditableContent page="team" section="overview" contentKey="location_label" defaultValue="Έδρα" tag="span" /></h3>
              <p className="text-xl font-black text-primary-600"><EditableContent page="team" section="overview" contentKey="location_value" defaultValue="Χανιά, Κρήτη" tag="span" /></p>
            </div>
            <div className="rounded-2xl border bg-gray-50 p-6 text-center">
              <SafeIcon icon={FiUsers} className="mx-auto mb-4 h-8 w-8 text-primary-600" />
              <h3 className="mb-2 font-semibold"><EditableContent page="team" section="overview" contentKey="category_label" defaultValue="Κατηγορία" tag="span" /></h3>
              <p className="text-xl font-black text-primary-600"><EditableContent page="team" section="overview" contentKey="category_value" defaultValue="Γ' Κατηγορία Γυναικών" tag="span" /></p>
            </div>
          </div>

          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-8">
            <h3 className="mb-4 text-2xl font-black"><EditableContent page="team" section="history" contentKey="title" defaultValue="Ιστορία" tag="span" /></h3>
            <div className="leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: siteConfig.team_history_text || 'Ο Α.Ο. Χανιά αποτελεί ιστορικό σύλλογο των Χανίων και συνεχίζει με επίκεντρο την ανάπτυξη του γυναικείου ποδοσφαίρου.' }} />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-secondary-900 via-primary-900 to-primary-700 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold"><SafeIcon icon={FiShield} className="h-4 w-4" />Διοίκηση</div>
            <h2 className="mb-4 text-3xl font-black md:text-5xl"><EditableContent page="team" section="board" contentKey="title" defaultValue="Διοικητικό Συμβούλιο" tag="span" /></h2>
            <p className="mx-auto max-w-2xl text-primary-100"><EditableContent page="team" section="board" contentKey="subtitle" defaultValue="Οι άνθρωποι που βρίσκονται πίσω από τη διοίκηση και την πορεία του συλλόγου." tag="span" /></p>
          </div>

          {staffLoading ? renderLoading(true) : boardMembers.length > 0 ? (
            <>
              {president && (
                <div className="mx-auto mb-12 max-w-5xl overflow-hidden rounded-3xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-xl">
                  <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
                    <div className="relative min-h-[300px] bg-white/5">
                      <ImageWithFallback src={president.image_url} alt={president.name} className="absolute inset-0 h-full w-full object-cover object-top" />
                      <div className="absolute bottom-4 left-4 rounded-full bg-yellow-400 px-3 py-1.5 text-xs font-black uppercase text-yellow-950">Πρόεδρος</div>
                    </div>
                    <div className="flex flex-col justify-center p-8 md:p-10">
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">Πρόεδρος Διοικητικού Συμβουλίου</p>
                      <h3 className="mb-3 text-3xl font-black md:text-4xl">{president.name}</h3>
                      <p className="text-lg font-semibold text-primary-100">{president.position}</p>
                      {president.bio && <p className="mt-5 leading-relaxed text-white/80">{president.bio}</p>}
                    </div>
                  </div>
                </div>
              )}

              <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {otherBoardMembers.map((member) => (
                  <div key={member.id} className="rounded-2xl border border-white/15 bg-white/10 p-6 text-center backdrop-blur-md">
                    <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full border-4 border-white/20"><ImageWithFallback src={member.image_url} alt={member.name} className="h-full w-full object-cover object-top" /></div>
                    <h3 className="text-xl font-black">{member.name}</h3>
                    <p className="mt-1 font-semibold text-primary-200">{member.position}</p>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-center text-white/60">Δεν υπάρχουν καταχωρημένα μέλη Διοικητικού Συμβουλίου.</p>}
        </div>
      </section>

      <section className="bg-gray-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50 px-4 py-2 text-sm font-bold text-primary-700"><SafeIcon icon={FiTarget} className="h-4 w-4" />Ποδοσφαιρικό Τμήμα</div>
            <h2 className="mb-4 text-3xl font-black md:text-4xl"><EditableContent page="team" section="staff" contentKey="title" defaultValue="Τεχνικό & Επιστημονικό Επιτελείο" tag="span" /></h2>
            <p className="mx-auto max-w-2xl text-gray-600"><EditableContent page="team" section="staff" contentKey="subtitle" defaultValue="Προπονητές και συνεργάτες που στηρίζουν καθημερινά την αγωνιστική λειτουργία της ομάδας." tag="span" /></p>
          </div>

          {staffLoading ? renderLoading() : (
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-4">
              {technicalMembers.map((member) => (
                <div key={member.id} className="overflow-hidden rounded-2xl border bg-white shadow-sm">
                  <div className="relative h-56 bg-gray-100"><ImageWithFallback src={member.image_url} alt={member.name} className="h-full w-full object-cover object-top" /></div>
                  <div className="p-5"><p className="text-lg font-black">{member.name}</p><p className="font-black text-primary-700">{member.position}</p>{member.email && <a href={`mailto:${member.email}`} className="mt-2 block break-all text-sm font-semibold text-gray-600 hover:text-primary-600">{member.email}</a>}{member.bio && <p className="mt-2 text-sm text-gray-600">{member.bio}</p>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-black md:text-4xl"><EditableContent page="team" section="players" contentKey="title" defaultValue="Οι Παίκτριες" tag="span" /></h2>
            <p className="mx-auto max-w-2xl text-gray-600"><EditableContent page="team" section="players" contentKey="subtitle" defaultValue="Γνώρισε τις αθλήτριές μας και τη διαδρομή τους με τον ΑΟ Χανιά." tag="span" /></p>
          </div>

          {playersLoading ? renderLoading() : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {sortedPlayers.map((player) => (
                <motion.article key={player.id} className="overflow-hidden rounded-2xl border bg-gray-50 shadow-md">
                  <button type="button" onClick={() => { setSelectedPlayer(player); setIsModalOpen(true); }} className="block w-full text-left">
                    <div className="relative h-56 bg-primary-600">
                      <ImageWithFallback src={player.image_url} alt={player.name} className="h-full w-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-5 text-white">{player.number && <div className="text-3xl font-black">#{player.number}</div>}<div className="text-sm font-semibold">{player.position}</div></div>
                      {player.is_featured && <div className="absolute right-4 top-4 flex items-center rounded-full bg-yellow-400 px-2.5 py-1 text-xs font-black text-yellow-950"><SafeIcon icon={FiStar} className="mr-1 h-3 w-3" />{player.featured_label || 'ΤΑΛΕΝΤΟ'}</div>}
                    </div>
                    <div className="p-6 pb-4">
                      <h3 className="mb-2 text-xl font-black">{player.name}</h3>
                      <p className="text-sm text-gray-600">{player.age ? `Ηλικία: ${player.age} • ` : ''}{player.position}</p>
                      {player.bio && <p className="mt-3 line-clamp-3 text-sm text-gray-700">{player.bio}</p>}
                      <div className="mt-4 inline-flex items-center text-xs font-black uppercase tracking-wider text-primary-600">Προφίλ παίκτριας<SafeIcon icon={FiArrowRight} className="ml-1.5 h-3.5 w-3.5" /></div>
                    </div>
                  </button>
                  <div className="border-t bg-white p-4"><PlayerSponsorBlock player={player} sourceLocation="team_player_card" /></div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>{isModalOpen && <PlayerModal player={selectedPlayer} isOpen={isModalOpen} onClose={closePlayer} />}</AnimatePresence>
    </div>
  );
};

export default TeamPage;
